import React, { useEffect, useRef, useState } from 'react';
import logo from '../../../icons/logo.png';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { IoMdPrint } from 'react-icons/io';
import { IoArrowBack } from 'react-icons/io5';
import dayjs from 'dayjs';
import { encodedImagesURI } from '../../../utils/helpers';
import { CircularProgress } from "@mui/material";
import paid1 from '../../../icons/paid1.png';
import BankVoucherLayout from '../../../base-components/BankVoucherLayout';
import CustomModal from '../../../base-components/CustomModal';
import Popup from '../../../base-components/Popup';

const BASE_URL = process.env.REACT_APP_BASE_URL;
function POSInvoiceView() {
  const { id } = useParams();
  const printRef = useRef();
  const reactToPrintFn = useReactToPrint({ contentRef: printRef });
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('');
  const [location_id, setLocationID] = useState('');
  const [company_id, setCompanyID] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [alertType, setAlertType] = useState('success');
  const [message, setMessage] = useState('');
  const [buttonLoading, setButtonLoading] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_method: 'Cash',
    reference_number: ''
  });
  const [discount, setDiscount] = useState(0);
  const [discountRemarks, setDiscountRemarks] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountRemarksState, setDiscountRemarksState] = useState('');

  useEffect(() => {
    const sessionStr = sessionStorage.getItem('user-info');
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      setRole(session.user.role);
      setCompanyID(session.user.company_id);
      setLocationID(session.user.location_id);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (company_id && id) {
      getOrder();
      getCompanyData();
    }
  }, [company_id, id]);

  const getCompanyData = async () => {
    try {
      const res = await fetch(BASE_URL + `company/${company_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      setCompanyData(data);
    } catch (error) {
      console.error('Error fetching company data:', error);
    }
  };

  const getOrder = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}pos/${id}`);
      const data = await response.json();
      if (response.ok) {
        // Handle both object and array response
        if (Array.isArray(data)) {
          setOrder(data[0]);
        } else if (data.order) {
          setOrder(data.order);
        } else {
          setOrder(data);
        }
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = () => {
    navigate('/pos');
  };

  useEffect(() => {
    if (order?.invoice_id) {
      setDiscountAmount(order.invoice_id.discount || 0);
      setDiscountRemarksState(order.invoice_id.discount_remarks || '');
    }
    if (isDiscountModalOpen && order?.invoice_id) {
      setDiscountAmount(order.invoice_id.discount || 0);
      setDiscountRemarksState(order.invoice_id.discount_remarks || '');
    }
  }, [order, isDiscountModalOpen]);

  const handleChangePaymentStatus = async () => {
    setButtonLoading(true);
    if (discount > (order?.invoice_id?.pending_amount || 0)) {
      setIsPopupOpen(true);
      setAlertType('error');
      setMessage('Discount cannot exceed pending amount');
      setButtonLoading(false);
      return;
    }

    if ((paymentData.payment_method !== 'Cash') && !paymentData.reference_number) {
      setIsPopupOpen(true);
      setAlertType('error');
      setMessage('Reference number is required for non-cash payments');
      setButtonLoading(false);
      return;
    }

    const data = {
      invoice_id: order.invoice_id._id,
      amount: order.invoice_id.pending_amount,
      payment_method: paymentData.payment_method,
      reference_number: paymentData.reference_number || null,
      discount: discount,
      discount_remarks: discountRemarks,
    };

    try {
      const response = await fetch(BASE_URL + `invoice/pos/payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const responseData = await response.json();
        setAlertType('error');
        setMessage(responseData.message || 'Error updating payment status');
        setIsModalOpen(false);
        resetFields();
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
        }, 3000);
      } else {
        const responseData = await response.json();
        setAlertType('success');
        setMessage(responseData.message || 'Payment status updated successfully');
        setIsModalOpen(false);
        resetFields();
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
        }, 3000);
        // Refresh order data
        getOrder();
      }
    } catch (error) {
      console.error('Error updating payment status:', error);
      setAlertType('error');
      setMessage('Failed to update payment status');
      setIsPopupOpen(true);
      setTimeout(() => {
        setIsPopupOpen(false);
      }, 3000);
    } finally {
      setButtonLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    setButtonLoading(true);
    if (Number(discountAmount) > (order?.invoice_id?.total_amount || 0)) {
      setIsPopupOpen(true);
      setAlertType('error');
      setMessage('Discount cannot exceed total amount');
      setButtonLoading(false);
      return;
    }

    const data = {
      invoice_id: order.invoice_id._id,
      discount: Number(discountAmount),
      discount_remarks: discountRemarksState,
    };

    try {
      const response = await fetch(BASE_URL + `invoice/pos/discount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const responseData = await response.json();
        setAlertType('error');
        setMessage(responseData.message || 'Error applying discount');
        setIsDiscountModalOpen(false);
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
        }, 3000);
      } else {
        const responseData = await response.json();
        setAlertType('success');
        setMessage(responseData.message || 'Discount applied successfully');
        setIsDiscountModalOpen(false);
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
        }, 3000);
        // Refresh order data
        getOrder();
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      setAlertType('error');
      setMessage('Failed to apply discount');
      setIsPopupOpen(true);
    } finally {
      setButtonLoading(false);
    }
  };

  const resetFields = () => {
    setPaymentData({
      payment_method: 'Cash',
      reference_number: ''
    });
    setDiscount(0);
    setDiscountRemarks('');
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    resetFields();
  };

  const getPrintTimestamp = () => {
    const now = new Date();
    return now.toString("MMM DD, YYYY HH:MM:SS A");
  };

  if (!order && !loading) {
    return (
      <div className="flex justify-center items-center mt-20">
        <h3 className="text-xl font-bold text-gx-ink-soft">Order not found</h3>
      </div>
    );
  }

  const invoice = order?.invoice_id || {};
  const voucher = order?.voucher_id || {};
  const member = order?.member_id || null;

  return (
    <>
      {loading ? (
        
          <div className="flex justify-center items-center mt-20 relative">
            {/* SVG Gradient Definition */}
            <svg width="0" height="0">
              <defs>
                <linearGradient id="gradientColors" x1="1" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D71920" />
                  <stop offset="100%" stopColor="#F7931E" />
                </linearGradient>
              </defs>
            </svg>
            {/* Circular Progress with Gradient Stroke */}
            <CircularProgress
              thickness={4}
              size={35}
              sx={{
                'svg circle': {
                  stroke: 'url(#gradientColors)', // Apply gradient stroke
                },
              }}
            />
          </div>
        
      ) : (
        <div ref={printRef} className="px-2 md:px-2 md:text-[16px] lg:px-10 lg:mb-16 print:p-0 print:m-0">
          {/* Action Buttons Bar */}
          <div className="flex justify-between items-center p-2 print:hidden sm:text-base xl:text-4xl">
            <div className="px-2 py-2">
              <IoArrowBack onClick={handleNavigate} className="cursor-pointer text-4xl print:hidden" />
            </div>
            <div className="flex items-center gap-4">
              {invoice?.is_paid === false && invoice?.payment_status !== 'Paid' && role !== 'User' && (
                <div className="flex gap-2 text-sm">
                  <span
                    className="bg-green-700 p-2 px-4 text-white font-bold rounded-full hover:cursor-pointer"
                    onClick={() => setIsModalOpen(true)}
                  >
                    Pay Invoice
                  </span>
                  <span
                    className="bg-gx-orange p-2 px-4 text-white font-bold rounded-full hover:cursor-pointer"
                    onClick={() => setIsDiscountModalOpen(true)}
                  >
                    Apply Discount
                  </span>
                </div>
              )}
              <IoMdPrint
                className="text-2xl hover:cursor-pointer md:text-4xl lg:text-4xl"
                onClick={() => reactToPrintFn()}
              />
            </div>
          </div>
          <BankVoucherLayout
            company_id={company_id}
            voucherData={{
              invoice_no: voucher?.invoice_no || order?.order_id,
              voucher_no: voucher?.invoice_no || order?.order_id,
              voucher_type: 'POS INVOICE',
              voucher_date: voucher?.voucher_date || order?.created_at,
              due_date: invoice?.due_date || order?.created_at,
              membership_fee: order?.subtotal,
              discount: invoice?.discount || order?.discount || 0,
              other_charges_amount: 0,
              other_charges_name: 'POS Order Items',
              registration_fee: 0,
              trainer_fee: 0,
              facilities_fee: 0,
              is_paid: invoice?.is_paid !== undefined ? invoice.is_paid : false,
              payment_status: invoice?.payment_status || 'Pending',
              payment_date: invoice?.payment_date || voucher?.payment_date || null,
              reference_number: voucher?.reference_number || order?.transaction_id,
              payment_method: voucher?.payment_method || order?.payment_method,
              transaction_number: voucher?.transaction_number,
              company_has_bank_integration: voucher?.company_has_bank_integration,
              invoice: {
                ...invoice,
                other_charges: invoice?.other_charges || (order?.items ? JSON.stringify({
                  name: 'POS Order Items',
                  items: order.items,
                  remarks: `POS Order: ${order?.order_id}`
                }) : null)
              },
              total_amount: invoice?.total_amount || order?.total_amount,
              pending_amount: invoice?.pending_amount || order?.total_amount
            }}
            companyData={companyData}
            memberData={{
              member_id: order?.member_id?.member_id,
              name: order?.member_id?.name || order?.customer_name || 'Walk-in Customer',
              phone: order?.member_id?.phone || order?.customer_phone,
              package_name: 'POS Order'
            }}
          >

        <div className="border-b-2 mb-10 flex justify-between items-center print:mx-0">
          <img
            src={companyData && companyData?.company_logo ? encodedImagesURI(companyData?.company_logo) : logo}
            alt=""
            className="text-center p-2 w-24 md:w-28 lg:w-32 lg:p-4 xl:w-40"
            style={{ objectFit: 'contain', maxHeight: '170px' }}
          />
          {invoice.is_paid === true && (
            <div className="">
              <img src={paid1} alt="Paid" />
            </div>
          )}
        </div>

        <div className="flex justify-between items-start mx-[11px] text-[8px] gap-4 print:mx-5 print:my-16 md:mx-4 md:text-sm md:gap-10 lg:text-sm xl:text-md xl:text-xl print:text-lg">
          <div className="w-1/3">
            <p>
              <strong className="mr-2">From:</strong>
              {companyData?.company_name || 'N/A'}
            </p>
            <p>
              <strong className="mr-2">Address:</strong>
              {companyData?.address || 'N/A'}
            </p>
            <p>
              <strong className="mr-2">Email:</strong>
              {companyData?.email || 'N/A'}
            </p>
            <p>
              <strong className="mr-2">Phone No:</strong>
              {companyData?.phone_number || 'N/A'}
            </p>
          </div>
          <div className="w-1/3">
            <p>
              <strong className="mr-2">To:</strong>
              {member?.name || order?.customer_name || 'Walk-in Customer'}
            </p>
            {member ? (
              <>
                <p>
                  <strong className="mr-2">Address: </strong>
                  {member?.address || 'N/A'}
                </p>
                <p>
                  <strong className="mr-2">Phone No: </strong>
                  {member?.phone || 'N/A'}
                </p>
                <p>
                  <strong className="mr-2">Email: </strong>
                  {member?.email || 'N/A'}
                </p>
                {member?.member_id && (
                  <p>
                    <strong className="mr-2">Member ID:</strong>
                    {member.member_id}
                  </p>
                )}
              </>
            ) : order?.customer_name ? (
              <>
                {order.customer_phone && (
                  <p>
                    <strong className="mr-2">Phone No: </strong>
                    {order.customer_phone}
                  </p>
                )}
                {order.customer_email && (
                  <p>
                    <strong className="mr-2">Email: </strong>
                    {order.customer_email}
                  </p>
                )}
                {order.customer_cnic && (
                  <p>
                    <strong className="mr-2">CNIC: </strong>
                    {order.customer_cnic}
                  </p>
                )}
              </>
            ) : null}
          </div>
          <div className="w-1/3">
            <p>
              <strong className="mr-2">Invoice Date: </strong>
              {voucher?.voucher_date
                ? dayjs(voucher.voucher_date).format('MMM DD, YYYY')
                : invoice?.created_at
                  ? dayjs(invoice.created_at).format('MMM DD, YYYY')
                  : dayjs(order.created_at).format('MMM DD, YYYY')}
            </p>
            <p>
              <strong className="mr-2">Invoice No:</strong>
              {voucher?.invoice_no || order.order_id || 'N/A'}
            </p>
            {voucher?.company_has_bank_integration === true && (
              <p>
                <strong className="mr-2">Bank Voucher Number:</strong>
                {voucher?.transaction_number || 'N/A'}
              </p>
            )}
            <p>
              <strong className="mr-2">Order ID:</strong>
              {order.order_id || 'N/A'}
            </p>
            {invoice?.payment_date && (
              <p>
                <strong className="mr-2">Paid Date:</strong>
                {dayjs(invoice.payment_date).format('MMM DD, YYYY')}
              </p>
            )}
          </div>
        </div>

        <div className="mt-10 print:mt-16 print:mb-5 mx-4 print:mx-5 font-bold text-[8px] md:text-sm lg:text-sm xl:text-xl print:text-lg">
          Items:
        </div>
        <div className="text-left mx-10 my-5 print:mx-5">
          <table className="w-full">
            <thead>
              <tr className="border-b-2">
                <th className="py-2 text-left">Item</th>
                <th className="py-2 text-center">Quantity</th>
                <th className="py-2 text-right">Unit Price</th>
                <th className="py-2 text-right">Discount</th>
                <th className="py-2 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody className="text-[8px] md:text-sm lg:text-sm xl:text-xl print:text-lg">
              {(() => {
                // Get items from order or from invoice other_charges
                let itemsToDisplay = order?.items || [];
                
                // Try to get items from invoice other_charges if available
                if (invoice?.other_charges) {
                  try {
                    const otherCharges = typeof invoice.other_charges === 'string' 
                      ? JSON.parse(invoice.other_charges) 
                      : invoice.other_charges;
                    if (otherCharges?.items && Array.isArray(otherCharges.items)) {
                      itemsToDisplay = otherCharges.items;
                    }
                  } catch (e) {
                    console.error('Error parsing other_charges:', e);
                  }
                }
                
                return itemsToDisplay.map((item, index) => (
                  <tr key={index} className="border-b-2">
                    <td className="py-2">{item.product_name || 'N/A'}</td>
                    <td className="py-2 text-center">{item.quantity}</td>
                    <td className="py-2 text-right">Rs. {item.unit_price?.toFixed(2) || 0}</td>
                    <td className="py-2 text-right">Rs. {item.discount?.toFixed(2) || 0}</td>
                    <td className="py-2 text-right">Rs. {item.subtotal?.toFixed(2) || 0}</td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>

        <div className="print:mt-16 print:mx-20 mx-0 flex flex-row flex justify-between text-[8px] md:text-base lg:text-sm print:text-lg xl:text-xl">
          <div className="flex flex-col mt-10 mx-12">
            <div className="flex items-center sm:gap-0 text-[8px] md:text-sm lg:text-sm xl:text-xl print:text-lg">
              <h1 className="font-bold p-2">Payment Methods:</h1>
              <p>{voucher?.payment_method || order.payment_method || (invoice?.is_paid ? 'Bank' : 'N/A')}</p>
            </div>
            {(voucher?.payment_method || order.payment_method) && (voucher?.payment_method !== 'Cash' || order.payment_method !== 'Cash') && (
              <div className="flex items-center sm:gap-0 text-[8px] md:text-sm lg:text-sm xl:text-xl print:text-lg">
                <h1 className="font-bold p-2">Payment Reference:</h1>
                <p>{voucher?.reference_number || order.transaction_id || order.reference_number || 'N/A'}</p>
              </div>
            )}
            {(discountRemarks || invoice?.discount_remarks || order.discount_remarks) && (
              <div className="flex items-center sm:gap-0 text-[8px] md:text-sm lg:text-sm xl:text-xl print:text-lg">
                <h1 className="font-bold p-2">Discount Remarks:</h1>
                <p>{discountRemarks || invoice?.discount_remarks || order.discount_remarks || <span className='text-gx-ink-soft'>No Discount Remarks</span>}</p>
              </div>
            )}
            {order.sold_by && (
              <div className="flex items-center sm:gap-0 text-[8px] md:text-sm lg:text-sm xl:text-xl print:text-lg">
                <h1 className="font-bold p-2">Sold By:</h1>
                <p>{order.sold_by.name || 'N/A'}</p>
              </div>
            )}
          </div>
          <div className="text-[8px] pr-4 md:text-sm lg:text-sm lg:pr-20 xl:text-xl xl:pr-28 print:text-lg">
            <table className="mt-5 w-full">
              <tr className="border-b-2">
                <td className="pb-2 font-bold pr-10">Subtotal:</td>
                <td className="pb-2">Rs. {order.subtotal?.toFixed(2) || 0}</td>
              </tr>
              <tr className="border-b-2">
                <td className="pb-2 font-bold pr-10">Subtotal:</td>
                <td className="pb-2">Rs. {invoice?.total_amount?.toFixed(2) || order.total_amount?.toFixed(2) || 0}</td>
              </tr>
              <tr className="border-b-2">
                <td className="pb-2 font-bold pr-10">Discount:</td>
                <td className="pb-2">Rs. {invoice?.discount?.toFixed(2) || order.discount?.toFixed(2) || 0}</td>
              </tr>
              <tr className="border-b-2">
                <td className="font-bold pt-2">Total:</td>
                <td className="pt-2">Rs. {invoice?.pending_amount?.toFixed(2) || (order.total_amount - (invoice?.discount || order.discount || 0))?.toFixed(2) || 0}</td>
              </tr>
              {invoice?.payment_status === 'Paid' ? (
                <tr className="border-b-2">
                  <td className="font-bold pt-2">Paid:</td>
                  <td className="pt-2">Rs. {voucher?.paid_amount?.toFixed(2) || (invoice?.total_amount - (invoice?.discount || 0))?.toFixed(2) || 0}</td>
                </tr>
              ) : (
                <>
                  <tr className="border-b-2">
                    <td className="font-bold pt-2">Balance:</td>
                    <td className="pt-2">Rs. 0</td>
                  </tr>
                  <tr>
                    <td className="font-bold pt-2">Pending:</td>
                    <td className="pt-2">Rs. {invoice?.pending_amount?.toFixed(2) || order.total_amount?.toFixed(2) || 0}</td>
                  </tr>
                </>
              )}
            </table>
          </div>
        </div>

            <div className="print:block hidden text-left text-[8px] text-black-500 mt-4 md:text-sm lg:text-sm xl:text-xl print:text-lg">
              Printed on: {getPrintTimestamp()}
            </div>
            <div className="mt-10 print:mt-16 text-center text-[8px] md:text-sm lg:text-sm xl:text-xl print:text-lg">
              <p className="text-gx-ink-soft italic">Thank you for your purchase!</p>
            </div>
          </BankVoucherLayout>
        </div>
      )}

      {/* Payment Modal */}
      <CustomModal isModal={isModalOpen} handleModal={handleModalClose} size={'xl'}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleChangePaymentStatus();
          }}
        >
          <div className="pt-5"></div>
          <div className="m-5 p-2 flex justify-center gap-10 text-gx-ink-soft border border-gx-border rounded-lg font-bold">
            <div className="flex justify-start items-center gap-2">
              <h1>Name:</h1>
              <p className="font-semibold">
                {member?.name || order?.customer_name || 'Walk-in Customer'}
              </p>
            </div>
            <div className="flex justify-start items-center gap-2">
              <h1>Amount:</h1>
              {discount > 0 ? (
                <>
                  <p className="text-sm text-gx-ink-soft line-through font-semibold">
                    {' '}
                    {invoice.pending_amount}
                  </p>
                  <p className="font-semibold">{invoice.pending_amount - discount}</p>
                </>
              ) : (
                <p className="font-semibold">{invoice.pending_amount}</p>
              )}
            </div>
          </div>
          <div className="mx-4 md:mx-7 text-gx-ink-soft flex flex-col gap-4">
            {/* Payment Method */}
            <div className="flex flex-col w-full gap-2">
              <label className="font-bold">Payment Method:</label>
              <select
                className="font-semibold border border-gx-border w-full p-2 rounded-lg outline-0"
                value={paymentData.payment_method}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, payment_method: e.target.value })
                }
              >
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="Online">Online</option>
                <option value="Jazzcash">JazzCash</option>
                <option value="Cheque">Cheque</option>
                <option value="Wallet">Wallet</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Reference Number (Conditional) */}
            {paymentData.payment_method !== 'Cash' && (
              <div className="flex flex-col w-full gap-2">
                <label className="font-bold">
                  Reference Number <span className="text-red-500">*</span>:
                </label>
                <input
                  type="text"
                  required
                  className="font-semibold border border-gx-border w-full p-2 rounded-lg outline-0"
                  placeholder="Enter reference number"
                  value={paymentData.reference_number || ''}
                  onChange={(e) =>
                    setPaymentData({ ...paymentData, reference_number: e.target.value })
                  }
                />
              </div>
            )}

            {/* Discount */}
            <div className="flex flex-col w-full gap-2">
              <label className="font-bold">Discount:</label>
              <input
                type="number"
                className="font-semibold border border-gx-border w-full p-2 rounded-lg outline-0"
                placeholder="Enter discount amount"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
              />
            </div>

            {/* Discount Remarks */}
            <div className="flex flex-col w-full gap-2">
              <label className="font-bold">Discount Remarks:</label>
              <input
                type="text"
                className="font-semibold border border-gx-border w-full p-2 rounded-lg outline-0"
                placeholder="Enter discount remarks"
                value={discountRemarks}
                onChange={(e) => setDiscountRemarks(e.target.value)}
              />
            </div>
          </div>

          <div className="m-5 text-center">
            <div className="flex justify-end text-center items-center gap-3 my-5">
              <button
                type="submit"
                className="flex w-full text-center justify-center bg-blue-900 p-2 px-6 border border-gx-border rounded-lg text-white font-medium hover:bg-blue-800"
                disabled={buttonLoading}
              >
                Pay Invoice
                {buttonLoading && (
                  <div className="ml-2 mt-1">
                    <CircularProgress size={20} color="#fff" />
                  </div>
                )}
              </button>
            </div>
          </div>
        </form>
      </CustomModal>

      {/* Discount Modal */}
      <CustomModal
        isModal={isDiscountModalOpen}
        handleModal={() => setIsDiscountModalOpen(false)}
        size={'xl'}
      >
        <h2 className="m-5 text-center text-xl font-medium text-gx-ink-soft">
          Apply/Adjust Discount
        </h2>
        <div className="mx-10 p-4 border border-gx-border rounded-lg">
          <div className="flex flex-col gap-3 text-gx-ink-soft">
            <div className="flex justify-between items-center bg-gx-canvas p-2 rounded">
              <span className="font-bold text-sm">Total Amount:</span>
              <span className="font-semibold">{order?.invoice_id?.total_amount || 0}</span>
            </div>
            <div className="flex justify-between items-center bg-gx-canvas p-2 rounded">
              <span className="font-bold text-sm">Existing Discount:</span>
              <span className="font-semibold text-red-600">
                - {order?.invoice_id?.discount || 0}
              </span>
            </div>
            <div className="flex justify-between items-center bg-gx-canvas p-2 rounded">
              <span className="font-bold text-sm">Current Pending:</span>
              <span className="font-semibold">{order?.invoice_id?.pending_amount || 0}</span>
            </div>

            <div className="border-t border-gx-border my-2"></div>

            <div className="flex flex-col gap-2">
              <label className="font-bold">Set New Total Discount:</label>
              <input
                type="number"
                className="font-semibold border border-blue-400 w-full p-2 rounded-lg outline-0 focus:ring-2 focus:ring-blue-200"
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
              />
              <p className="text-xs text-gx-ink-soft italic">
                * This will replace the previous discount amount.
              </p>
            </div>

            <div className="flex justify-between items-center bg-green-50 p-3 rounded border border-green-200 mt-2">
              <span className="font-bold text-green-800">New Pending Amount (Preview):</span>
              <span className="font-bold text-green-800 text-lg">
                {Math.max(
                  0,
                  (order?.invoice_id?.pending_amount || 0) -
                    (Number(discountAmount) - (order?.invoice_id?.discount || 0))
                )}
              </span>
            </div>

            <div className="flex flex-col gap-2 mt-2">
              <label className="font-bold">Discount Remarks:</label>
              <input
                type="text"
                className="font-semibold border border-gx-border w-full p-2 rounded-lg outline-0"
                placeholder="Reason for discount"
                value={discountRemarksState}
                onChange={(e) => setDiscountRemarksState(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="m-5 text-center">
          <div className="flex justify-center items-center gap-3 my-5">
            <button
              className={`bg-green-600 p-2 px-8 border border-gx-border rounded-lg text-white font-medium hover:bg-green-700 ${
                buttonLoading ? 'cursor-not-allowed bg-gray-400' : ''
              }`}
              onClick={handleApplyDiscount}
              disabled={buttonLoading}
            >
              Apply
              {buttonLoading && (
                <div className="ml-2 mt-1">
                  <CircularProgress size={20} color="#fff" />
                </div>
              )}
            </button>
            <button
              className="bg-red-600 p-2 px-8 border border-gx-border rounded-lg text-white font-medium hover:bg-red-700"
              onClick={() => setIsDiscountModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </CustomModal>

      {/* Popup */}
      {isPopupOpen && (
        <Popup alertType={alertType} message={message} onClose={() => setIsPopupOpen(false)} />
      )}
    </>
  );
}

export default POSInvoiceView;

